import React, { useState } from 'react';
import { apiClient } from '../../api';

interface ConfigurationTabProps {
  tenantId: string;
  did: string;
  apiEndpoint: string;
  ourDid: string;
  onDidChange: (newDid: string) => Promise<void>;
}

const ConfigurationTab: React.FC<ConfigurationTabProps> = ({
  tenantId,
  did,
  apiEndpoint,
  ourDid,
  onDidChange,
}) => {
  const [editingDid, setEditingDid] = useState(false);
  const [newDid, setNewDid] = useState(did);
  const [originalDid, setOriginalDid] = useState(did);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Update newDid and originalDid when did prop changes (after save)
  React.useEffect(() => {
    setNewDid(did);
    setOriginalDid(did);
  }, [did]);

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const response = await apiClient.post('/mpx-tenants/test-connection', {
        did: newDid,
      });

      if (response.data.success) {
        setTestResult({
          success: true,
          message: 'Connection successful! MPX control plane is reachable and authenticated.',
        });
      } else {
        setTestResult({
          success: false,
          message: response.data.message || 'Connection test failed',
        });
      }
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.message || 'Failed to connect to MPX control plane',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveDid = async () => {
    if (newDid !== originalDid) {
      await onDidChange(newDid);
      setOriginalDid(newDid);
    }
    setEditingDid(false);
  };

  const handleCancelEdit = () => {
    setNewDid(originalDid);
    setEditingDid(false);
    setTestResult(null);
  };

  return (
    <div className="tab-pane-content">
      <div className="form-group">
        <label htmlFor="did">
          MPX Control Plane DID <span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            id="did"
            value={newDid}
            onChange={(e) => setNewDid(e.target.value)}
            readOnly={!editingDid}
            disabled={!editingDid}
          />
          <div className="input-group-append">
            {!editingDid ? (
              <button
                className="btn btn-outline-primary"
                type="button"
                onClick={() => setEditingDid(true)}
              >
                <i className="fas fa-edit"></i> Edit
              </button>
            ) : (
              <>
                <button
                  className="btn btn-success"
                  type="button"
                  onClick={handleSaveDid}
                >
                  <i className="fas fa-check"></i> Save
                </button>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={handleCancelEdit}
                >
                  <i className="fas fa-times"></i> Cancel
                </button>
              </>
            )}
          </div>
        </div>
        <small className="form-text text-muted">
          The DID of the MPX control plane. Changing this will require re-authentication.
        </small>
      </div>

      <div className="form-group">
        <button
          type="button"
          className="btn btn-info"
          onClick={handleTestConnection}
          disabled={testing || !newDid}
        >
          {testing ? (
            <>
              <i className="fas fa-spinner fa-spin me-2"></i> Testing Connection...
            </>
          ) : (
            <>
              <i className="fas fa-heartbeat me-2"></i> Test Connection</>
          )}
        </button>

        {testResult && (
          <div className={`alert alert-${testResult.success ? 'success' : 'warning'} mt-3`}>
            <i className={`fas fa-${testResult.success ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
            {testResult.message}
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="api_endpoint">API Endpoint</label>
        <input
          type="text"
          className="form-control"
          id="api_endpoint"
          value={apiEndpoint}
          readOnly
          disabled
        />
        <small className="form-text text-muted">
          Resolved from the MPX Control Plane DID (read-only)
        </small>
      </div>

      <div className="form-group">
        <label htmlFor="our_did">Our DID</label>
        <input
          type="text"
          className="form-control"
          id="our_did"
          value={ourDid}
          readOnly
          disabled
        />
        <small className="form-text text-muted">
          The did:peer we use to communicate with this MPX tenant (read-only)
        </small>
      </div>
    </div>
  );
};

export default ConfigurationTab;
